/**
 * What the partner-webinar landing page shows — decided from the ENGINE's
 * own lifecycle, not the CC feed's status enum.
 *
 * CC's `status` stays 'announced' forever in practice: nothing moves it to
 * 'replay' or 'done' after the event. Reading it alone meant PW01 (Dext,
 * 13 Aug) and PW02 (Plooto, 24 Sep) both kept a "Save your seat" form on
 * their landing pages for weeks after they ran (mct_99ebb10c-87a). The
 * worker already ships `replay.phase` from effectiveWebinarReplayPhase(),
 * which knows the event has ended, so that wins whenever it is present.
 * The feed status is only the fallback for an older worker payload.
 */

export type WebinarFeedStatus = 'announced' | 'live' | 'replay' | 'done';

export interface WebinarReplayBlock {
  phase: 'pre-event' | 'replay' | 'archived';
  available: boolean;
  gated: boolean;
  url: string | null;
}

export interface WebinarLandingInput {
  slug: string;
  status: WebinarFeedStatus;
  replay?: WebinarReplayBlock | null;
  /** @deprecated external-recording link from older worker payloads. */
  replay_url?: string | null;
}

export type WebinarLandingMode =
  | 'register'        // before / during the event
  | 'replay-ready'    // ended, recording open to registrants
  | 'replay-pending'  // ended, recording not published yet
  | 'ended';          // ended, replay window closed (or never had one)

export interface WebinarLanding {
  mode: WebinarLandingMode;
  canRegister: boolean;
  /** Where "Watch the replay" goes: the gated page for an Engine recording,
   *  the partner's own link for an external one. Null when nothing is open. */
  replayHref: string | null;
}

export function webinarLanding(w: WebinarLandingInput): WebinarLanding {
  const r = w.replay ?? null;
  if (r) {
    if (r.phase === 'pre-event') {
      return { mode: 'register', canRegister: w.status !== 'done' && w.status !== 'replay', replayHref: null };
    }
    if (r.phase === 'replay') {
      if (r.available) {
        const href = r.url ?? `/webinars/${encodeURIComponent(w.slug)}/replay`;
        return { mode: 'replay-ready', canRegister: false, replayHref: href };
      }
      return { mode: 'replay-pending', canRegister: false, replayHref: null };
    }
    return { mode: 'ended', canRegister: false, replayHref: null };
  }
  // Older payload with no replay block: the feed enum is all we have.
  if (w.status === 'announced' || w.status === 'live') return { mode: 'register', canRegister: true, replayHref: null };
  if (w.status === 'replay') {
    return w.replay_url
      ? { mode: 'replay-ready', canRegister: false, replayHref: w.replay_url }
      : { mode: 'replay-pending', canRegister: false, replayHref: null };
  }
  return { mode: 'ended', canRegister: false, replayHref: null };
}
